import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  Settings,
  Wallet,
  AlertCircle,
  Trophy,
  ArrowRight,
  Globe
} from 'lucide-react';
import BotonVolver from './BotonVolver';
import BotonWhatsApp from './BotonWhatsApp';

export default function Rgturnos() {
  const [t] = useTranslation("global");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-black to-zinc-900 text-zinc-100 font-sans selection:bg-zinc-500/30 pb-20">
      <BotonVolver text={t("rgturnos.volver")} />

      <main className="relative">
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 px-6 text-center">
          <div className="container mx-auto max-w-4xl">
            <h1 className="text-6xl md:text-7xl font-messina tracking-wide text-white mb-8 leading-tight">
              {t("rgturnos.title")}
            </h1>
            <p className="text-xl md:text-2xl text-zinc-400 font-light mb-12 max-w-3xl mx-auto leading-relaxed italic">
              "{t("rgturnos.subtitle")}"
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://rgturnos.com.ar"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 px-8 py-4 bg-white text-black hover:bg-gray-200 rounded-lg transition-all duration-300 font-bold text-lg"
              >
                <Globe className="w-5 h-5" />
                {t("rgturnos.visit-btn")}
              </a>
              <BotonWhatsApp
                text={t("rgturnos.action-btn")}
                message="¡Hola! Me interesa obtener más información sobre RG Turnos y Torneos."
              />
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="py-10 px-6">
          <div className="container mx-auto max-w-4xl bg-zinc-900/30 border border-zinc-800/50 p-10 rounded-3xl text-center">
            <p className="text-lg md:text-xl text-zinc-300 font-light leading-relaxed">
              {t("rgturnos.intro")}
            </p>
          </div>
        </section>

        {/* Features & Problems */}
        <section className="py-20 px-6">
          <div className="container mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Features */}
            <div className="space-y-8">
              <h2 className="text-3xl md:text-4xl font-messina tracking-wide text-white flex items-center gap-4">
                <Settings className="text-zinc-500" />
                {t("rgturnos.features-title")}
              </h2>
              <div className="grid gap-4">
                {[1, 2, 3, 4, 5].map((num) => (
                  <div key={num} className="flex items-center gap-4 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800/50">
                    <CheckCircle2 className="text-zinc-500 w-5 h-5 flex-shrink-0" />
                    <span className="text-lg font-light text-zinc-300">{t(`rgturnos.feature-${num}`)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Problems */}
            <div className="space-y-8">
              <h2 className="text-3xl md:text-4xl font-messina tracking-wide text-white flex items-center gap-4">
                <AlertCircle className="text-zinc-500" />
                {t("rgturnos.problems-title")}
              </h2>
              <div className="grid gap-4">
                {[1, 2, 3, 4, 5].map((num) => (
                  <div key={num} className="flex items-start gap-4 p-4 bg-zinc-900/20 rounded-xl border border-dashed border-zinc-800">
                    <div className="mt-1.5 w-1.5 h-1.5 bg-zinc-600 rounded-full flex-shrink-0" />
                    <span className="text-lg font-light text-zinc-400 italic">{t(`rgturnos.problem-${num}`)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="py-20 px-6 bg-zinc-900/20 border-y border-zinc-900">
          <div className="container mx-auto max-w-5xl text-center">
            <h2 className="text-3xl md:text-4xl font-messina tracking-wide text-white mb-16">
              {t("rgturnos.steps-title")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((num) => (
                <div key={num} className="relative p-8 bg-zinc-900/40 rounded-2xl border border-zinc-800/50">
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-zinc-800 rounded-full flex items-center justify-center text-xl font-bold border border-zinc-700">
                    {num}
                  </div>
                  <p className="mt-4 text-lg font-light text-zinc-200">
                    {t(`rgturnos.step-${num}`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Torneos */}
        <section className="py-24 px-6">
          <div className="container mx-auto max-w-5xl overflow-hidden rounded-[3rem] border border-zinc-800 bg-gradient-to-br from-zinc-900 to-black p-12 md:p-20 relative">
            <div className="absolute top-0 right-0 p-10 opacity-5">
              <Trophy size={200} />
            </div>
            <div className="relative z-10 text-center md:text-left">
              <h2 className="text-4xl md:text-5xl font-messina tracking-wide text-white mb-8">
                {t("rgturnos.torneos-title")}
              </h2>
              <p className="text-xl text-zinc-400 font-light leading-relaxed mb-10 max-w-3xl">
                {t("rgturnos.torneos-text")}
              </p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                {["Ligas", "Eliminación Directa", "Fases de Grupos", "Fixtures Dinámicos", "Tablas en Vivo"].map((tag, i) => (
                  <span key={i} className="px-4 py-2 bg-zinc-800/50 rounded-full text-sm text-zinc-300 border border-zinc-700/50">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
